/**
 * A rate card's controls fields (TMMPDM-0088; the affiliate maximums since TMMPDM-0089): the Controls
 * tab schedules them, and since TMMPDM-0090 the Add new rate card dialog can set the first row.
 */
import type { SettingField } from '@/components/settings/settingFields';
import { useCopy } from '@/hooks/useCopy';

export function useCardControlFields(): SettingField[] {
  const { t } = useCopy();
  return [
    {
      key: 'maxAiSpendMinor',
      label: t('cardSettings.controls.maxAiSpend', 'Maximum AI spend per person per month'),
      help: t(
        'cardSettings.controls.maxAiSpendHelp',
        "The most one person can spend on AI in a month. A person's own lower limit still applies.",
      ),
      kind: 'money',
      min: 0.01,
    },
    {
      key: 'aiDailyCap',
      label: t('cardSettings.controls.aiDailyCap', 'Daily AI cap'),
      help: t(
        'cardSettings.controls.aiDailyCapHelp',
        'The most AI calls one person can make in a day.',
      ),
      kind: 'integer',
      min: 1,
    },
    {
      key: 'aiMaxInflight',
      label: t('cardSettings.controls.aiMaxInflight', 'AI calls at once'),
      help: t(
        'cardSettings.controls.aiMaxInflightHelp',
        'How many AI calls one person can have running at the same time.',
      ),
      kind: 'integer',
      min: 1,
    },
    {
      key: 'aiMaxAutoRecharges30d',
      label: t('cardSettings.controls.autoRecharges', 'Automatic recharges per 30 days'),
      help: t(
        'cardSettings.controls.autoRechargesHelp',
        '0 means the wallet never recharges itself; the person tops up by hand.',
      ),
      kind: 'integer',
      min: 0,
      max: 100,
    },
    {
      key: 'graceDaysAfterEnd',
      label: t('cardSettings.controls.graceDays', 'Grace days after a subscription ends'),
      help: t(
        'cardSettings.controls.graceDaysHelp',
        'Read when grace starts. A subscription already in grace keeps its end date.',
      ),
      kind: 'integer',
      min: 0,
      max: 90,
    },
    {
      key: 'affiliateRecurringMaxBp',
      label: t('cardSettings.controls.affiliateRecurringMax', 'Highest affiliate recurring rate'),
      help: t(
        'cardSettings.controls.affiliateRecurringMaxHelp',
        "The highest rate in any phase of a reseller's commission plan. Checked when a plan is created or activated; active plans aren't changed.",
      ),
      kind: 'percentBp',
      min: 0,
      max: 100,
    },
    {
      key: 'affiliateBountyMaxMinor',
      label: t('cardSettings.controls.affiliateBountyMax', 'Highest affiliate bounty'),
      help: t(
        'cardSettings.controls.affiliateBountyMaxHelp',
        'The largest one-time bounty a commission plan may offer per referral.',
      ),
      kind: 'money',
      min: 0,
    },
    {
      key: 'affiliateMilestoneMaxMinor',
      label: t('cardSettings.controls.affiliateMilestoneMax', 'Highest milestone bonus'),
      help: t(
        'cardSettings.controls.affiliateMilestoneMaxHelp',
        'The largest single milestone bonus a commission plan may offer.',
      ),
      kind: 'money',
      min: 0,
    },
    {
      key: 'affiliateCoolOffMaxDays',
      label: t('cardSettings.controls.affiliateCoolOffMax', 'Longest affiliate cool-off'),
      help: t(
        'cardSettings.controls.affiliateCoolOffMaxHelp',
        'The longest cool-off, in days, a commission plan may set.',
      ),
      kind: 'integer',
      min: 0,
      max: 365,
    },
  ];
}
